
import React from 'react';
import { NavLink } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { FaFacebook, FaInstagram, FaLinkedin, FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const logoText = '/images/logo.webp';

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  return (
    <footer className="bg-brand-dark text-white pt-20 pb-10 border-t border-white/5">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Info */}
          <div>
            <NavLink to={isEn ? "/en/" : "/"} className="flex items-center mb-6">
              <img
                src={logoText}
                alt="Pato Yazılım Logo"
                className="h-12 w-auto object-contain"
                width="180"
                height="48"
              />
            </NavLink>
            <p className="text-gray-400 leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a href="https://wa.me/message/RWB5MCIBD74WH1" target="_blank" rel="noreferrer" aria-label="WhatsApp iletişim" className="w-12 h-12 rounded-full bg-white/5 hover:bg-brand-blue flex items-center justify-center transition-colors">
                <FaWhatsapp size={20} />
              </a>
              <a href="https://www.instagram.com/patoyazilim" target="_blank" rel="noreferrer" aria-label="Instagram sayfamız" className="w-12 h-12 rounded-full bg-white/5 hover:bg-brand-blue flex items-center justify-center transition-colors">
                <FaInstagram size={20} />
              </a>
              <a href="https://www.linkedin.com/company/pato-yazilim" target="_blank" rel="noreferrer" aria-label="LinkedIn sayfamız" className="w-12 h-12 rounded-full bg-white/5 hover:bg-brand-blue flex items-center justify-center transition-colors">
                <FaLinkedin size={20} />
              </a>
              <a href="https://www.facebook.com/patoyazilim" target="_blank" rel="noreferrer" aria-label="Facebook sayfamız" className="w-12 h-12 rounded-full bg-white/5 hover:bg-brand-blue flex items-center justify-center transition-colors">
                <FaFacebook size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t('footer.quickLinks')}</h3>
            <ul className="space-y-4">
              <li><NavLink to={isEn ? "/en/about" : "/hakkimizda"} className="block py-2 text-gray-400 hover:text-brand-blue transition-colors">{t('nav.about')}</NavLink></li>
              <li><NavLink to={isEn ? "/en/services" : "/hizmetlerimiz"} className="block py-2 text-gray-400 hover:text-brand-blue transition-colors">{t('nav.services')}</NavLink></li>
              {/* <li><NavLink to={isEn ? "/en/demos" : "/demolar"} className="block py-2 text-gray-400 hover:text-brand-blue transition-colors">Demolar</NavLink></li> */}
              <li><NavLink to={isEn ? "/en/blog" : "/blog"} className="block py-2 text-gray-400 hover:text-brand-blue transition-colors">{t('nav.blog')}</NavLink></li>
              <li><NavLink to={isEn ? "/en/faq" : "/sss"} className="block py-2 text-gray-400 hover:text-brand-blue transition-colors">{t('nav.faq')}</NavLink></li>
              <li><NavLink to={isEn ? "/en/contact" : "/iletisim"} className="block py-2 text-gray-400 hover:text-brand-blue transition-colors">{t('nav.contact')}</NavLink></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t('footer.services')}</h3>
            <ul className="space-y-2">
              <li><NavLink to={isEn ? "/en/services" : "/hizmetlerimiz"} className="block py-2 text-gray-400 hover:text-brand-blue transition-colors">Web Geliştirme</NavLink></li>
              <li><NavLink to={isEn ? "/en/services" : "/hizmetlerimiz"} className="block py-2 text-gray-400 hover:text-brand-blue transition-colors">Mobil Uygulama</NavLink></li>
              <li><NavLink to={isEn ? "/en/services" : "/hizmetlerimiz"} className="block py-2 text-gray-400 hover:text-brand-blue transition-colors">Özel Yazılım</NavLink></li>
              <li><NavLink to={isEn ? "/en/services" : "/hizmetlerimiz"} className="block py-2 text-gray-400 hover:text-brand-blue transition-colors">Analiz ve Raporlama</NavLink></li>
              <li><NavLink to={isEn ? "/en/services" : "/hizmetlerimiz"} className="block py-2 text-gray-400 hover:text-brand-blue transition-colors">IT Danışmanlık</NavLink></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t('footer.contact')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="text-brand-blue shrink-0 mt-1" size={18} />
                <span>Bozkurt Mah. Seymen Sk. No:43 D:3 Şişli/İstanbul</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400">
                <Phone className="text-brand-blue shrink-0" size={18} />
                <span>+90 545 276 17 46</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400">
                <Mail className="text-brand-blue shrink-0" size={18} />
                <a href="mailto:info@patoyazilim.com" className="hover:text-white transition-colors">info@patoyazilim.com</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Pato Yazılım. {t('common.allRightsReserved')}
          </p>
          <div className="flex gap-6 text-sm text-gray-500">
            <NavLink to={isEn ? "/en/privacy-policy" : "/gizlilik-politikasi"} className="hover:text-white transition-colors py-2">Gizlilik Politikası</NavLink>
            <NavLink to={isEn ? "/en/terms-of-use" : "/kullanim-sartlari"} className="hover:text-white transition-colors py-2">Kullanım Şartları</NavLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;